import { useMutation } from '@apollo/client';
import type { CustomAttributeData } from '@risksmart-app/domain/src/types/common.types';
import type { UpdateObligationImpactMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { UpdateObligationImpactDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import { useUpdateObligationImpactTRPC } from './useUpdateObligationImpactTRPC';

type UpdateObligationImpactInput = {
  Id: string;
  Description: string;
  ImpactRating: number;
  CustomAttributeData?: CustomAttributeData | null;
};

export const useUpdateObligationImpact = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  const [updateObligationImpactGraphQL, graphqlState] = useMutation(
    UpdateObligationImpactDocument,
    {
      update: (cache) => {
        evictField(cache, 'obligation_impact');
        evictField(cache, 'obligation');
      },
    }
  );

  const trpcMutation = useUpdateObligationImpactTRPC();

  const updateObligationImpact = async (
    variables: UpdateObligationImpactInput
  ): Promise<UpdateObligationImpactMutation> => {
    if (trpcEnabled) {
      return trpcMutation.updateObligationImpact(variables);
    }

    const result = await updateObligationImpactGraphQL({
      variables: {
        id: variables.Id,
        Description: variables.Description,
        ImpactRating: variables.ImpactRating,
        CustomAttributeData: variables.CustomAttributeData ?? null,
      },
    });

    if (!result.data) {
      throw new Error('Failed to update obligation impact');
    }

    return result.data;
  };

  return {
    updateObligationImpact,
    loading: trpcEnabled ? trpcMutation.loading : graphqlState.loading,
    error: trpcEnabled ? trpcMutation.error : graphqlState.error,
  };
};
